import { useOrderedCol } from '../lib/hooks';
import type { ToolDoc } from '../lib/data-model';
import { usePomodoro } from '../lib/pomodoro';
import { Loading, ErrorBlock, EmptyState } from '../components/ui';

export default function ToolsPage() {
  const tools = useOrderedCol<ToolDoc>('tools');
  const pomo = usePomodoro();

  if (tools.error) return <ErrorBlock message={tools.error} />;
  if (tools.loading) return <Loading label="Loading tools…" />;

  const visible = tools.data.filter(t => !t.hidden);

  return (
    <div className="page-anim">
      <h1 className="page-title" style={{ marginBottom: 10 }}>Recommended tools</h1>
      <p className="lede" style={{ marginBottom: 30 }}>Things students in this course actually use. Nothing here is required.</p>

      {visible.length === 0 ? (
        <EmptyState
          glyph="✦"
          title="No tools yet"
          body="Handpicked study tools will show up here once the teacher adds them."
        />
      ) : (
        <div className="grid-cards">
          {visible.map(t => {
            const isPomo = /pomodoro|pomofocus/i.test(`${t.name} ${t.url ?? ''}`);
            return (
              <div className="card hoverable" key={t.id} style={{ padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div
                    aria-hidden
                    style={{
                      width: 40, height: 40, borderRadius: 13,
                      background: t.iconBg ?? 'var(--t-green)', color: t.iconFg ?? 'var(--c-green)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: 'var(--font-serif)', fontSize: 19, fontWeight: 600
                    }}
                  >
                    {t.glyph ?? '✦'}
                  </div>
                  {isPomo ? (
                    <span className="mono" style={{ fontSize: 10.5, letterSpacing: '.08em', color: 'var(--c-plum)' }}>IN-APP</span>
                  ) : (
                    <span className="mono muted" style={{ fontSize: 10.5, letterSpacing: '.08em' }}>{t.tag.toUpperCase()}</span>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 600, letterSpacing: '-.01em', marginBottom: 6 }}>{t.name}</div>
                  <div style={{ fontSize: 14, lineHeight: 1.5, color: 'var(--ink2)', textWrap: 'pretty' }}>{t.blurb}</div>
                </div>
                <div style={{ flex: 1 }} />
                {isPomo ? (
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button className="btn btn-dark btn-sm" style={{ alignSelf: 'flex-start' }} onClick={pomo.openTool}>
                      Open timer
                    </button>
                    <button
                      className="btn btn-outline btn-sm"
                      style={{ alignSelf: 'flex-start' }}
                      onClick={() => { pomo.openTool(); pomo.start(); }}
                    >
                      Start {pomo.settings.focusMin}-min focus
                    </button>
                  </div>
                ) : (
                  <a
                    className="btn btn-outline btn-sm"
                    style={{ alignSelf: 'flex-start' }}
                    href={t.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open tool ↗
                  </a>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
